// ./auth.service.ts
import api from "./api";
import { IAdmin } from "../types/admin.types";
import i18n from "../i18n";

// Define the response structure for login and register API calls
interface AuthResponse {
  token: string;
  status: string;
  data: {
    accessToken: string;
    expiresIn: string;
  };
}

interface AdminData {
  username: string;
  password: string;
  [key: string]: any;
}

class AuthService {
  // Login method
  public async login(
    username: string,
    password: string
  ): Promise<{ authResponse: AuthResponse; adminUser: IAdmin }> {
    try {
      const response = await api.post<AuthResponse>("/admins/login", {
        username,
        password,
      });
      
      // Save the token in localStorage
      this.saveToken(response.data.data?.accessToken);
      
      // Fetch admin user data
      const adminUser = await this.getCurrentAdmin();
      
      return {
        authResponse: response.data,
        adminUser,
      };
    } catch (error: any) {
      // Handle error properly
      console.error("Login error:", error);
      this.handleError(error, i18n.t("serviceErrors.loginFailed"));
    }
  }
  
  // Get current admin data
  public async getCurrentAdmin(): Promise<IAdmin> {
    try {
      const response = await api.get<{ status: string; data: IAdmin }>("/admins/me");
      return response.data.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.fetchAdminData"));
    }
  }

  // Register admin method
  public async registerAdmin(adminData: AdminData): Promise<AuthResponse> {
    try {
      const response = await api.post<AuthResponse>(
        "/admins/register",
        adminData
      );
      return response.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.registrationFailed"));
    }
  }

  // Logout method
  public logout(): void {
    api.post("/admins/logout", {}).catch(() => {});
    localStorage.removeItem("token");
    localStorage.removeItem("adminData");
    window.location.href = "/login";
  }

  // Save token in localStorage
  private saveToken(token: string): void {
    localStorage.setItem("token", token);
  }

  // Error handling method to keep things DRY (Don't Repeat Yourself)
  private handleError(error: any, defaultMessage: string): never {
    if (error.response && error.response.data) {
      throw new Error(error.response.data.message || defaultMessage);
    } else {
      throw new Error(i18n.t("serviceErrors.serverError"));
    }
  }

  // Optionally, you can add a method to check if a user is logged in
  public isAuthenticated(): boolean {
    return !!localStorage.getItem("token");
  }

  // Fetch current user info
  public async getMe(): Promise<any> {
    try {
      const response = await api.get("/me");
      return response.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.fetchUserInfo"));
    }
  }
}

const authServiceInstance = new AuthService();
export default authServiceInstance;
